import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Sidebar,
  StatusBar,
  ThemeToggle,
  AmbientOrbs,
  DEFAULT_NAV_ITEMS,
  type NavItem,
  type VehicleOnlineState,
} from '@riviamigo/ui/primitives';
import { getUnitSystem } from '@riviamigo/ui/lib/utils';
import {
  useAuth,
  useCurrentVehicleStatus,
  useMe,
  useResolvedVehicleSelection,
  useVehicleStatus,
} from '@riviamigo/hooks';
import { isVehicleCharging } from '@riviamigo/types';
import { CalendarClock, Loader2, LogOut, Settings, TriangleAlert, UserCog, Wifi, WifiOff } from 'lucide-react';
import { GiRestingVampire } from 'react-icons/gi';
import {
  TbBattery1,
  TbBattery2,
  TbBattery3,
  TbBattery4,
  TbBatteryCharging,
  TbBatteryOff,
  TbCarSuv,
} from 'react-icons/tb';
import { FaTruckPickup } from 'react-icons/fa6';
import { emitToast } from '../feedback/toast';
import { useThemePreferenceController } from '../../hooks/useThemePreferenceController';
import {
  getRivianCredentialRenewalNotice,
  type RivianCredentialRenewalNotice,
} from '../../lib/rivianCredentialRenewal';

interface AppLayoutProps {
  children: React.ReactNode;
  activeKey: string;
}

interface FeedHealthStatus {
  auth_state?: string | null;
  auth_reason_code?: string | null;
  worker_health?: string | null;
  worker_health_msg?: string | null;
  telemetry_stale?: boolean;
  telemetry_stale_reason?: string | null;
}

const FEED_HEALTH_TOAST_COOLDOWN_MS = 15 * 60 * 1000;

function CredentialRenewalNotice({
  notice,
  compact = false,
  onClick,
}: {
  notice: RivianCredentialRenewalNotice;
  compact?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={notice.message}
      aria-label={`${notice.label}. ${notice.message}`}
      className={compact
        ? '-mx-1 flex h-8 w-[calc(100%+0.5rem)] items-center justify-center rounded-lg bg-bg-elevated text-status-warning transition-colors hover:bg-bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent'
        : 'flex min-h-10 w-full items-center gap-2 rounded-lg bg-bg-elevated px-3 py-2 text-start text-status-warning transition-colors hover:bg-bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent'}
    >
      <CalendarClock className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!compact ? <span className="min-w-0 text-sm font-medium leading-5">{notice.label}</span> : null}
    </button>
  );
}

export function getFeedHealthIssue(status?: FeedHealthStatus | null) {
  if (status?.auth_state === 'needs_reauth') {
    return {
      key: `needs_reauth:${status.auth_reason_code ?? 'unknown'}`,
      title: 'Rivian feed disconnected',
      message:
        status.worker_health_msg ??
        'Rivian credentials are missing or expired. Reconnect the vehicle from Settings → Vehicles.',
    };
  }
  if (
    status?.worker_health === 'error' ||
    status?.worker_health === 'stale' ||
    status?.worker_health === 'degraded'
  ) {
    return {
      key: `unhealthy:${status?.worker_health ?? 'unknown'}:${status?.telemetry_stale_reason ?? 'unknown'}`,
      title: 'Vehicle feed unhealthy',
      message:
        status?.worker_health_msg ??
        'Riviamigo is not receiving fresh vehicle telemetry. Open Health for collector details.',
    };
  }
  return null;
}

export function shouldEmitFeedHealthToast(storage: Storage, key: string, now = Date.now()) {
  const lastEmittedAt = Number(storage.getItem(key));
  if (Number.isFinite(lastEmittedAt) && now - lastEmittedAt < FEED_HEALTH_TOAST_COOLDOWN_MS) {
    return false;
  }
  storage.setItem(key, String(now));
  return true;
}

export function resolveVehicleOnlineState({
  liveVehicleId,
  vehicleSelectionReady,
  connectionState,
  connected,
  feedHealthIssue,
}: {
  liveVehicleId: string | null;
  vehicleSelectionReady: boolean;
  connectionState: string;
  connected: boolean;
  feedHealthIssue: ReturnType<typeof getFeedHealthIssue>;
}): VehicleOnlineState {
  if (!liveVehicleId) return 'offline';
  if (!vehicleSelectionReady) return 'connecting';
  // Browser-to-Riviamigo transport is the first boundary. A stale or failed
  // browser socket must not surface an old server-side feed error as if it
  // were the current connection state.
  if (!connected || connectionState === 'failed') return 'connecting';
  if (feedHealthIssue) return 'unhealthy';
  return 'online';
}

function getCompactBatteryIcon(socPercent: number) {
  if (socPercent > 75) return { Component: TbBattery4, variant: 'four' };
  if (socPercent > 50) return { Component: TbBattery3, variant: 'three' };
  if (socPercent > 25) return { Component: TbBattery2, variant: 'two' };
  if (socPercent > 5) return { Component: TbBattery1, variant: 'one' };
  return { Component: TbBatteryOff, variant: 'off' };
}

export function AppLayout({ children, activeKey }: AppLayoutProps) {
  const navigate = useNavigate();
  const accessToken = useAuth((s) => s.accessToken);
  const logout = useAuth((s) => s.logout);
  const { effectiveVehicleId, vehicleSelectionReady, vehicles } = useResolvedVehicleSelection();
  const me = useMe();
  const canAccessUsers = me.data?.role === 'admin' || me.data?.role === 'super_user';
  const liveVehicleId = vehicleSelectionReady ? effectiveVehicleId : null;
  const liveAccessToken = vehicleSelectionReady ? accessToken : null;
  const {
    status: liveStatus,
    connected,
    connectionState,
  } = useVehicleStatus(liveVehicleId, liveAccessToken);
  const { data: currentStatus } = useCurrentVehicleStatus(liveVehicleId);
  const status = currentStatus ?? liveStatus;
  const themeController = useThemePreferenceController();
  const [unitSystem, setUnitSystem] = React.useState(() => getUnitSystem());
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('rm-sidebar-collapsed') === 'true';
  });

  const setPersistedSidebarCollapsed = React.useCallback((nextCollapsed: boolean) => {
    setSidebarCollapsed(nextCollapsed);
    localStorage.setItem('rm-sidebar-collapsed', String(nextCollapsed));
  }, []);

  // Stable reference so both addEventListener and removeEventListener receive the
  // same function identity even if React re-renders between the two calls.
  const handleUnitsChange = React.useCallback(() => {
    setUnitSystem(getUnitSystem());
  }, []);

  const feedHealthIssue = getFeedHealthIssue(currentStatus);
  const renewalNotice = getRivianCredentialRenewalNotice(currentStatus);
  React.useEffect(() => {
    if (!liveVehicleId || !feedHealthIssue || !connected) return;

    const storageKey = `rm-feed-health-toast:${liveVehicleId}:${feedHealthIssue.key}`;
    const emitIfDue = () => {
      if (!shouldEmitFeedHealthToast(localStorage, storageKey)) return;
      emitToast({
        title: feedHealthIssue.title,
        message: feedHealthIssue.message,
        variant: 'error',
      });
    };

    emitIfDue();
    const interval = window.setInterval(emitIfDue, 60_000);
    return () => window.clearInterval(interval);
  }, [
    connected,
    feedHealthIssue?.key,
    feedHealthIssue?.message,
    feedHealthIssue?.title,
    liveVehicleId,
  ]);

  React.useEffect(() => {
    window.addEventListener('rm-units-change', handleUnitsChange as EventListener);
    window.addEventListener('storage', handleUnitsChange);
    return () => {
      window.removeEventListener('rm-units-change', handleUnitsChange as EventListener);
      window.removeEventListener('storage', handleUnitsChange);
    };
  }, [handleUnitsChange]);

  const onlineState = resolveVehicleOnlineState({
    liveVehicleId,
    vehicleSelectionReady,
    connectionState,
    connected,
    feedHealthIssue,
  });
  const compactBatteryLevel =
    typeof status?.battery_level === 'number' ? status.battery_level : undefined;
  const compactIsCharging = isVehicleCharging(status);
  const showCompactBattery = compactBatteryLevel !== undefined && onlineState === 'online';
  const compactBatteryIcon = showCompactBattery
    ? compactIsCharging
      ? { Component: TbBatteryCharging, variant: 'charging' }
      : getCompactBatteryIcon(compactBatteryLevel)
    : undefined;
  const collapsedFooterRow =
    '-mx-1 grid w-[calc(100%+0.5rem)] grid-cols-[24px_24px] items-center justify-between';
  const collapsedStatusRow = compactBatteryIcon
    ? collapsedFooterRow
    : 'flex w-full items-center justify-center';
  const collapsedFooterCell = 'flex h-8 w-6 items-center justify-center';
  const sidebarItems = React.useMemo<NavItem[]>(() => {
    const firstVehicleModel = vehicles[0]?.model?.toUpperCase() ?? '';
    const overviewIcon = firstVehicleModel.includes('R1T') ? (
      <FaTruckPickup className="h-[1.125rem] w-[1.125rem]" />
    ) : (
      <TbCarSuv className="h-[1.125rem] w-[1.125rem]" />
    );
    const inBatterySection = activeKey === 'battery' || activeKey.startsWith('battery.');
    return DEFAULT_NAV_ITEMS.map((item) => {
      if (item.key === 'dashboard') {
        return {
          ...item,
          icon: overviewIcon,
        };
      }
      if (item.key !== 'battery' || !inBatterySection) return item;
      return {
        ...item,
        children: [
          {
            key: 'battery.phantom-drain',
            label: 'Phantom Drain',
            href: '/battery/phantom-drain',
            icon: <GiRestingVampire className="h-3.5 w-3.5" />,
          },
        ],
      };
    }).concat(
      canAccessUsers
        ? [
            {
              key: 'users',
              label: 'Users',
              href: '/users',
              icon: <UserCog className="h-[1.125rem] w-[1.125rem]" />,
              pinToBottom: true,
            },
          ]
        : []
    );
  }, [activeKey, vehicles, canAccessUsers]);

  async function handleLogout() {
    await logout();
    navigate({ to: '/login' });
  }

  return (
    <div className="min-h-screen bg-bg-page text-fg" data-unit-system={unitSystem}>
      <AmbientOrbs />

      <Sidebar
        activeKey={activeKey}
        onNavigate={(href) => navigate({ to: href })}
        items={sidebarItems}
        mobileHeaderSlot={<ThemeToggle mode={themeController.mode} onModeChange={themeController.onModeChange} disabled={themeController.isPending} variant="ghost" className="h-11 w-11" ariaLabel="Theme options" />}
        collapsed={sidebarCollapsed}
        onCollapsedChange={setPersistedSidebarCollapsed}
        bottomSlot={({ collapsed, mobile, closeMobileNavigation }) =>
          mobile ? (
            <div className="flex flex-col gap-3">
              {renewalNotice ? (
                <CredentialRenewalNotice
                  notice={renewalNotice}
                  onClick={() => {
                    navigate({ to: '/settings' });
                    closeMobileNavigation(false);
                  }}
                />
              ) : null}
              <StatusBar
                onlineState={onlineState}
                socPercent={status?.battery_level ?? undefined}
                isCharging={isVehicleCharging(status)}
                rangeEstimateMi={status?.range_miles ?? undefined}
                className="h-12 px-4"
                size="menu"
              />

              <button
                type="button"
                onClick={() => {
                  navigate({ to: '/settings' });
                  closeMobileNavigation(false);
                }}
                aria-label="Open settings"
                className="flex h-12 w-full items-center gap-3 rounded-lg px-4 text-sm font-medium text-fg-secondary transition-colors hover:bg-bg-elevated hover:text-fg"
              >
                <Settings className="h-5 w-5 shrink-0" />
                <span>Settings</span>
              </button>

              <button
                type="button"
                onClick={async () => {
                  closeMobileNavigation(false);
                  await handleLogout();
                }}
                aria-label="Sign out"
                className="flex h-12 w-full items-center gap-3 rounded-lg px-4 text-sm font-medium text-fg-secondary transition-colors hover:bg-bg-elevated hover:text-fg"
              >
                <LogOut className="h-5 w-5 shrink-0" />
                <span>Sign out</span>
              </button>
            </div>
          ) : collapsed ? (
            <div className="flex w-full flex-col gap-2">
              <div className={collapsedStatusRow} data-collapsed-status-row>
                <div
                  className={collapsedFooterCell}
                  title={`Vehicle status: ${
                    onlineState === 'online'
                      ? 'Online'
                      : onlineState === 'connecting'
                        ? 'Reconnecting...'
                        : onlineState === 'unhealthy'
                          ? 'Feed unhealthy'
                          : onlineState === 'error'
                            ? 'Connection failed'
                            : 'Offline'
                  }`}
                  aria-label="Vehicle status"
                >
                  {onlineState === 'connecting' ? (
                    <Loader2 className="h-4 w-4 animate-spin text-accent" />
                  ) : onlineState === 'online' ? (
                    <Wifi className="h-4 w-4 text-status-positive" />
                  ) : onlineState === 'unhealthy' ? (
                    <TriangleAlert className="h-4 w-4 text-status-danger" />
                  ) : onlineState === 'error' ? (
                    <WifiOff className="h-4 w-4 text-status-danger" />
                  ) : (
                    <WifiOff className="h-4 w-4 text-fg-tertiary" />
                  )}
                </div>
                <div
                  className={collapsedFooterCell}
                  title={
                    showCompactBattery
                      ? `Battery status: ${Math.round(compactBatteryLevel)}%`
                      : 'Battery status unavailable'
                  }
                  aria-label="Battery status"
                >
                  {compactBatteryIcon && (
                    <compactBatteryIcon.Component
                      className={`h-4 w-4 ${
                        compactBatteryIcon.variant === 'charging'
                          ? 'text-accent'
                          : (compactBatteryLevel ?? 0) > 50
                            ? 'text-status-positive'
                            : (compactBatteryLevel ?? 0) > 20
                              ? 'text-status-warning'
                              : 'text-status-danger'
                      }`}
                      data-battery-icon={`tb-battery-${compactBatteryIcon.variant}`}
                    />
                  )}
                </div>
              </div>

              {renewalNotice ? (
                <CredentialRenewalNotice
                  compact
                  notice={renewalNotice}
                  onClick={() => navigate({ to: '/settings' })}
                />
              ) : null}

              <button
                type="button"
                onClick={() => navigate({ to: '/settings' })}
                title="Settings"
                aria-label="Open settings"
                className="-mx-1 flex h-8 w-[calc(100%+0.5rem)] items-center justify-center rounded-lg text-fg-tertiary transition-colors hover:bg-bg-elevated hover:text-fg"
              >
                <Settings className="h-4 w-4 shrink-0" />
              </button>

              <div className={collapsedFooterRow}>
                <button
                  type="button"
                  onClick={handleLogout}
                  title="Sign out"
                  aria-label="Sign out"
                  className="flex h-8 w-6 items-center justify-center rounded-lg text-fg-tertiary transition-colors hover:bg-bg-elevated hover:text-fg"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                </button>
                <ThemeToggle mode={themeController.mode} onModeChange={themeController.onModeChange} disabled={themeController.isPending} variant="ghost" className="h-8 w-8" ariaLabel="Theme options" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {renewalNotice ? (
                <CredentialRenewalNotice
                  notice={renewalNotice}
                  onClick={() => navigate({ to: '/settings' })}
                />
              ) : null}
              <StatusBar
                onlineState={onlineState}
                socPercent={status?.battery_level ?? undefined}
                isCharging={isVehicleCharging(status)}
                rangeEstimateMi={status?.range_miles ?? undefined}
                compact={collapsed}
              />

              <button
                type="button"
                onClick={() => navigate({ to: '/settings' })}
                title="Settings"
                aria-label="Open settings"
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-fg-tertiary transition-colors hover:bg-bg-elevated hover:text-fg"
              >
                <Settings className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-medium">Settings</span>
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleLogout}
                  title="Sign out"
                  aria-label="Sign out"
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-fg-tertiary transition-colors hover:bg-bg-elevated hover:text-fg"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-medium">Sign out</span>
                </button>
                <ThemeToggle mode={themeController.mode} onModeChange={themeController.onModeChange} disabled={themeController.isPending} variant="ghost" className="h-8 w-8" ariaLabel="Theme options" />
              </div>
            </div>
          )
        }
      />

      {/* Main content: offset by sidebar width on lg+ */}
      <main
        className={`rm-app-main transition-all duration-200 ${sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64'}`}
      >
        <div className="rm-app-content p-4 pt-14 sm:p-6 sm:pt-14 lg:p-6 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
